(function () {
  const STATE_NAMES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
  };

  const svgCache = new Map();
  let currentHeroStates = null;

  function fetchSvg(url) {
    if (!svgCache.has(url)) {
      svgCache.set(
        url,
        fetch(url, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) {
            throw new Error('Map SVG failed to load');
          }

          return response.text();
        })
      );
    }

    return svgCache.get(url);
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character];
    });
  }

  function normalizeCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function getStateCode(path) {
    const stateClass = Array.from(path.classList).find((className) => /^[a-z]{2}$/.test(className));
    return stateClass ? stateClass.toUpperCase() : '';
  }

  function getPointForState(path) {
    const svg = path.ownerSVGElement;

    if (!svg || typeof svg.createSVGPoint !== 'function' || typeof path.getBBox !== 'function') {
      return null;
    }

    const box = path.getBBox();
    const point = svg.createSVGPoint();
    point.x = box.x + box.width / 2;
    point.y = box.y + box.height / 2;

    try {
      return point.matrixTransform(path.getScreenCTM());
    } catch (error) {
      return null;
    }
  }

  function getTooltipHtml(state) {
    const statusText = state.detail ? `${state.statusLabel} • ${state.detail}` : state.statusLabel;
    const law = state.lawName
      ? `<span class="epr-us-map__tooltip-law">${escapeHtml(state.lawName)}</span>`
      : '';

    return `
      <span class="epr-us-map__tooltip-state">${escapeHtml(state.name)}</span>
      ${law}
      <span class="epr-us-map__tooltip-status">${escapeHtml(statusText)}</span>
    `;
  }

  function positionTooltip(context, clientX, clientY) {
    const rect = context.mapWrap.getBoundingClientRect();
    const tooltip = context.tooltip;
    const margin = 10;
    const offset = 18;
    const tooltipRect = tooltip.getBoundingClientRect();
    let x = clientX - rect.left + offset;
    let y = clientY - rect.top + offset;

    if (x + tooltipRect.width + margin > rect.width) {
      x = clientX - rect.left - tooltipRect.width - offset;
    }

    if (y + tooltipRect.height + margin > rect.height) {
      y = clientY - rect.top - tooltipRect.height - offset;
    }

    x = Math.max(margin, Math.min(x, rect.width - tooltipRect.width - margin));
    y = Math.max(margin, Math.min(y, rect.height - tooltipRect.height - margin));

    tooltip.style.setProperty('--epr-tooltip-x', `${x}px`);
    tooltip.style.setProperty('--epr-tooltip-y', `${y}px`);
  }

  function showTooltip(context, path, event, keepSelected) {
    const code = path.dataset.state;
    const state = context.statesByCode[code];

    if (!state) {
      return;
    }

    context.tooltip.style.setProperty('--epr-tooltip-accent', '#65B1C6');
    context.tooltip.innerHTML = getTooltipHtml(state);
    context.root.classList.add('is-tooltip-visible');

    if (event && typeof event.clientX === 'number') {
      positionTooltip(context, event.clientX, event.clientY);
      return;
    }

    const point = getPointForState(path);

    if (point) {
      positionTooltip(context, point.x, point.y);
    }

    if (keepSelected) {
      context.selectedPath = path;
    }
  }

  function clearSelection(context) {
    if (context.selectedPath) {
      context.selectedPath.classList.remove('is-selected');
      context.selectedPath = null;
    }

    context.root.classList.remove('is-tooltip-visible');
  }

  function getMapData(root) {
    const dataScript = root.querySelector('[data-epr-us-map-data]');

    if (!dataScript) {
      return null;
    }

    try {
      return JSON.parse(dataScript.textContent);
    } catch (error) {
      return null;
    }
  }

  function buildStateData(config) {
    const colors = config.colors || {};
    const labels = config.labels || {};
    const statesByCode = {};

    Object.keys(STATE_NAMES).forEach((code) => {
      statesByCode[code] = {
        code,
        name: STATE_NAMES[code],
        status: 'none',
        statusLabel: labels.noneTooltip || labels.none || 'No EPR action',
        color: colors.none || '#d9d9d9',
        lawName: '',
        detail: '',
        tooltipEnabled: true,
      };
    });

    (config.states || []).forEach((item) => {
      const code = normalizeCode(item.code);
      const status = item.status || 'none';

      if (!statesByCode[code]) {
        return;
      }

      statesByCode[code] = {
        code,
        name: STATE_NAMES[code],
        status,
        statusLabel: status === 'none' ? labels.noneTooltip || labels.none : labels[status] || statesByCode[code].statusLabel,
        color: colors[status] || statesByCode[code].color,
        lawName: item.lawName || '',
        detail: item.detail || '',
        tooltipEnabled: item.tooltipEnabled !== false,
      };
    });

    return statesByCode;
  }

  function syncHeroMap(statesByCode) {
    if (!statesByCode) {
      return;
    }

    document.querySelectorAll('[data-epr-hero-map] svg').forEach((svg) => {
      svg.querySelectorAll('.state path').forEach((path) => {
        const code = getStateCode(path);
        const state = statesByCode[code];

        if (!state) {
          return;
        }

        path.dataset.state = code;
        path.dataset.status = state.status;
        path.style.setProperty('--epr-state-fill', state.color);
      });
    });
  }

  function decorateSvg(context) {
    const svg = context.mapWrap.querySelector('svg');

    if (!svg) {
      throw new Error('Map SVG markup was not found');
    }

    svg.classList.add('epr-us-map__svg');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', context.config.ariaLabel || 'US packaging EPR legislation map');

    if (!svg.getAttribute('viewBox')) {
      svg.setAttribute('viewBox', '0 0 959 593');
    }

    svg.querySelectorAll('.state path').forEach((path) => {
      const code = getStateCode(path);
      const state = context.statesByCode[code];

      if (!state) {
        return;
      }

      path.dataset.state = code;
      path.dataset.status = state.status;
      path.style.setProperty('--epr-state-fill', state.color);

      if (!state.tooltipEnabled) {
        path.setAttribute('aria-label', `${state.name}: ${state.statusLabel}`);
        path.addEventListener('pointerenter', () => clearSelection(context));
        path.addEventListener('click', (event) => {
          event.stopPropagation();
          clearSelection(context);
        });
        return;
      }

      path.setAttribute('tabindex', '0');
      path.setAttribute('role', 'button');
      path.setAttribute('aria-label', `${state.name}: ${state.statusLabel}${state.detail ? `. ${state.detail}` : ''}`);

      path.addEventListener('pointerenter', (event) => {
        if (!context.selectedPath) {
          showTooltip(context, path, event, false);
        }
      });

      path.addEventListener('pointermove', (event) => {
        if (!context.selectedPath) {
          positionTooltip(context, event.clientX, event.clientY);
        }
      });

      path.addEventListener('pointerleave', () => {
        if (!context.selectedPath) {
          context.root.classList.remove('is-tooltip-visible');
        }
      });

      path.addEventListener('focus', () => {
        showTooltip(context, path, null, false);
      });

      path.addEventListener('blur', () => {
        if (!context.selectedPath) {
          context.root.classList.remove('is-tooltip-visible');
        }
      });

      path.addEventListener('click', (event) => {
        event.stopPropagation();

        if (context.selectedPath === path) {
          clearSelection(context);
          return;
        }

        if (context.selectedPath && context.selectedPath !== path) {
          context.selectedPath.classList.remove('is-selected');
        }

        context.selectedPath = path;
        path.classList.add('is-selected');
        showTooltip(context, path, event, true);
      });

      path.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();

        if (context.selectedPath === path) {
          clearSelection(context);
          return;
        }

        if (context.selectedPath && context.selectedPath !== path) {
          context.selectedPath.classList.remove('is-selected');
        }

        context.selectedPath = path;
        path.classList.add('is-selected');
        showTooltip(context, path, null, true);
      });
    });
  }

  function initMap(root) {
    if (!root || root.dataset.eprMapInitialized === 'true') {
      return;
    }

    const config = getMapData(root);
    const mapWrap = root.querySelector('[data-epr-us-map-canvas]');
    const tooltip = root.querySelector('[data-epr-us-map-tooltip]');

    if (!config || !mapWrap || !tooltip) {
      return;
    }

    root.dataset.eprMapInitialized = 'true';

    const context = {
      root,
      mapWrap,
      tooltip,
      config,
      statesByCode: buildStateData(config),
      selectedPath: null,
    };

    currentHeroStates = context.statesByCode;
    syncHeroMap(currentHeroStates);

    root.style.setProperty('--epr-map-collecting', config.colors.collecting);
    root.style.setProperty('--epr-map-passed', config.colors.passed);
    root.style.setProperty('--epr-map-active', config.colors.active);
    root.style.setProperty('--epr-map-none', config.colors.none);

    if (mapWrap.querySelector('svg')) {
      decorateSvg(context);
    } else if (config.svgUrl) {
      fetchSvg(config.svgUrl)
        .then((svgText) => {
          mapWrap.innerHTML = svgText + tooltip.outerHTML;
          context.tooltip = mapWrap.querySelector('[data-epr-us-map-tooltip]');
          decorateSvg(context);
        })
        .catch(() => {
          mapWrap.innerHTML = `<div class="epr-us-map__error">${escapeHtml(
            config.errorText || 'Map unavailable'
          )}</div>`;
        });
    } else {
      mapWrap.innerHTML = `<div class="epr-us-map__error">${escapeHtml(config.errorText || 'Map unavailable')}</div>`;
    }

    root.addEventListener('click', (event) => {
      if (!event.target.closest('[data-state]')) {
        clearSelection(context);
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        clearSelection(context);
      }
    });
  }

  function initAll(container) {
    (container || document).querySelectorAll('[data-epr-us-map]').forEach(initMap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    syncHeroMap(currentHeroStates);
  });
})();
